// src/components/extensions/ExtensionRequestModal.jsx
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FiClock, FiX } from 'react-icons/fi';
import apiService from '../../services/api';

const MIN_LEN = 20;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 1rem;
  animation: fadeIn 0.15s ease-out;
  @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
`;

const Modal = styled.div`
  background: var(--background-primary, #fff);
  border-radius: 14px;
  width: 100%;
  max-width: 480px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.22);
  font-family: 'Inter', system-ui, sans-serif;
  animation: slideUp 0.18s ease-out;
  @keyframes slideUp { from { transform: translateY(8px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
`;

const Header = styled.header`
  padding: 18px 20px 14px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  h3 { margin: 0; font-size: 1rem; font-weight: 600; color: var(--text-primary); }
`;

const IconBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: rgba(178, 125, 92, 0.12);
  color: #A86A4A;
`;

const CloseBtn = styled.button`
  background: transparent;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  &:hover { background: rgba(0,0,0,0.04); color: var(--text-primary); }
`;

const Body = styled.div`
  padding: 16px 20px 20px;
`;

const Field = styled.div`
  margin-bottom: 14px;
  &:last-child { margin-bottom: 0; }
`;

const Label = styled.label`
  display: block;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-muted);
  margin-bottom: 6px;
`;

const ReadOnly = styled.div`
  font-size: 14px;
  color: var(--text-primary);
  font-weight: 500;
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 110px;
  padding: 10px 12px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.45;
  color: var(--text-primary);
  background: #fff;
  resize: vertical;
  box-sizing: border-box;
  &:focus {
    outline: none;
    border-color: var(--primary-color, #B8956A);
    box-shadow: 0 0 0 3px rgba(184, 149, 106, 0.14);
  }
`;

const Hint = styled.p`
  margin: 6px 0 0 0;
  font-size: 12px;
  color: ${(p) => (p.$error ? '#A86A4A' : 'var(--text-light)')};
`;

const Footer = styled.footer`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 20px 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
`;

const Btn = styled.button`
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  padding: 8px 14px;
  border-radius: 8px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: background 0.15s ease, transform 0.15s ease;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const Cancel = styled(Btn)`
  background: transparent;
  color: var(--text-primary);
  border-color: rgba(0, 0, 0, 0.12);
  &:hover:not(:disabled) { background: rgba(0,0,0,0.03); }
`;

const Primary = styled(Btn)`
  background: var(--primary-color, #B8956A);
  color: #fff;
  &:hover:not(:disabled) { background: var(--primary-dark, #9A7A4F); transform: translateY(-1px); }
`;

const ErrorMsg = styled.div`
  margin-top: 8px;
  padding: 8px 10px;
  background: rgba(178, 125, 92, 0.10);
  border: 1px solid rgba(178, 125, 92, 0.30);
  border-radius: 6px;
  color: #8C5638;
  font-size: 12.5px;
`;

export default function ExtensionRequestModal({
  isOpen,
  onClose,
  onSubmitted,
  itemType,        // 'assignment' | 'quiz'
  itemId,
  itemTitle,
  courseName,
}) {
  const [justification, setJustification] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) { setJustification(''); setError(''); }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const trimmedLen = justification.trim().length;
  const tooShort = trimmedLen < MIN_LEN;
  const remaining = Math.max(0, MIN_LEN - trimmedLen);

  const handleSubmit = async () => {
    if (tooShort || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      await apiService.requestExtension({ itemType, itemId, justification: justification.trim() });
      onSubmitted?.();
      onClose?.();
    } catch (err) {
      setError(err?.errorData?.message || err?.message || 'Failed to submit request.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Overlay onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <Modal role="dialog" aria-label="Request extension">
        <Header>
          <Title>
            <IconBadge><FiClock size={16} /></IconBadge>
            <h3>Request Extension</h3>
          </Title>
          <CloseBtn type="button" onClick={onClose} aria-label="Close">
            <FiX size={18} />
          </CloseBtn>
        </Header>

        <Body>
          {courseName && (
            <Field>
              <Label>Course</Label>
              <ReadOnly>{courseName}</ReadOnly>
            </Field>
          )}
          {itemTitle && (
            <Field>
              <Label>{itemType === 'quiz' ? 'Quiz' : 'Assignment'}</Label>
              <ReadOnly>{itemTitle}</ReadOnly>
            </Field>
          )}

          <Field>
            <Label htmlFor="ext-justification">Please explain why you are requesting an extension</Label>
            <Textarea
              id="ext-justification"
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              maxLength={2000}
              autoFocus
            />
            <Hint $error={tooShort && trimmedLen > 0}>
              {tooShort
                ? `${remaining} more character${remaining === 1 ? '' : 's'} required (minimum ${MIN_LEN}).`
                : `${trimmedLen} characters.`}
            </Hint>
          </Field>

          {error && <ErrorMsg>{error}</ErrorMsg>}
        </Body>

        <Footer>
          <Cancel type="button" onClick={onClose} disabled={submitting}>Cancel</Cancel>
          <Primary type="button" onClick={handleSubmit} disabled={tooShort || submitting}>
            {submitting ? 'Submitting…' : 'Submit Request'}
          </Primary>
        </Footer>
      </Modal>
    </Overlay>
  );
}
